import { Page } from "@src/shared/pages";
import jwt_decode from "jwt-decode";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  CompanyTokenSchema,
  TokenData,
  VendorCompanyTokenSchema,
  VendorTokenData,
} from "@src/sudozi-schemas/token-data-schemas";
import { Spinner } from "@src/components/elements";
import { notifyError } from "@src/shared/notify";
import { authContext, UseAuthType } from "@src/contexts/authContext";

function useQuery() {
  return new URLSearchParams(window.location.search);
}

function getCompanyUserFromJWT(
  jwt: string | { data: TokenData },
): TokenData | null {
  if (!jwt) return null;
  const jwtData = typeof jwt === "string" ? jwt_decode(jwt) : jwt;

  const maybeCompanyTokenSchema = CompanyTokenSchema.safeParse(jwtData);

  if (maybeCompanyTokenSchema.success) {
    return maybeCompanyTokenSchema.data.data;
  }

  return null;
}

function getVendorUserFromJWT(
  jwt: string | { data: TokenData },
): VendorTokenData | null {
  if (!jwt) return null;
  const jwtData = typeof jwt === "string" ? jwt_decode(jwt) : jwt;

  const maybeVendorCompanyTokenSchema =
    VendorCompanyTokenSchema.safeParse(jwtData);

  if (maybeVendorCompanyTokenSchema.success) {
    const {
      data: { userId, email, firstName, lastName, vendorCompanyId, companyId },
    } = maybeVendorCompanyTokenSchema.data;

    return {
      userId,
      email,
      firstName,
      lastName,
      vendorCompanyId,
      companyId,
    };
  }

  return null;
}

export const AuthProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [loadedJwt, setLoadedJwt] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState<TokenData | null>(null);
  const [vendorUser, setVendorUser] = useState<VendorTokenData | null>(null);
  const query = useQuery();
  const loginTokenQuery = query.get("token");

  const removeToken = () => {
    setUser(null);
    setVendorUser(null);
  };

  const storeUserData = useCallback((token: string | { data: TokenData }) => {
    const companyUser = getCompanyUserFromJWT(token);
    if (companyUser) {
      setUser(companyUser);
      setVendorUser(null);
      return;
    }
    const vendorUser = getVendorUserFromJWT(token);
    if (vendorUser) {
      setVendorUser(vendorUser);
      setUser(null);
      return;
    }

    console.error("Unable to parse jwt data");
    removeToken();
  }, []);

  const updateCurrentUserState = useCallback(async () => {
    const response = await fetch("/jwt-data", {
      method: "get",
    });
    if (!response.ok) {
      setError(response.statusText);
      notifyError("Unable to fetch user data");
      return;
    }
    const result = await response.json();
    storeUserData(result);
    setLoadedJwt(true);
  }, [storeUserData]);

  useEffect(() => {
    void updateCurrentUserState();
  }, [updateCurrentUserState]);

  useEffect(() => {
    async function fetchToken(loginToken: string | null) {
      if (!loginToken) {
        setError("missing login token!");
        return;
      }

      const usersTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch("/create-jwt-from-login-token", {
        method: "post",
        body: JSON.stringify({
          loginTokenId: loginToken,
          timezone: usersTimezone,
        }),
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        setError(response.statusText);
        const { message, email } = await response.json();
        location.href = `${Page.LOGIN}?message=${encodeURIComponent(
          message ?? "An unknown error occurred. Please try again later.",
        )}&email=${encodeURIComponent(email)}`;
      }
      await updateCurrentUserState();
    }

    void fetchToken(loginTokenQuery);
  }, [loginTokenQuery, storeUserData, updateCurrentUserState]);

  return (
    <authContext.Provider
      value={{
        tokenUserData: user,
        vendorUser,
        updateCurrentUserState,
        removeToken,
        error,
        loadedJwt: loadedJwt,
      }}
    >
      {loadedJwt ? children : <Spinner />}
    </authContext.Provider>
  );
};

export const useAuth = (): UseAuthType => {
  return useContext(authContext);
};
